import { apiClient } from '@/features/platform/api/client'
import { helpers } from '@/utils/helpers'

function base64UrlToBuffer (base64Url: string): ArrayBuffer {
  const padding = '='.repeat((4 - base64Url.length % 4) % 4)
  const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  const buffer = new ArrayBuffer(raw.length)
  const view = new Uint8Array(buffer)

  for (let i = 0; i < raw.length; i++) {
    view[i] = raw.charCodeAt(i)
  }

  return buffer
}

function isStandalone (): boolean {
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true
  }

  return Boolean((navigator as Navigator & { standalone?: boolean }).standalone)
}

class PushService {
  isPushEnabled (): boolean {
    return import.meta.env.VITE_PUSH_ENABLED === 'true'
  }

  isPushSupported (): boolean {
    if (!this.isPushEnabled()) {
      return false
    }

    return 'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
  }

  isStandaloneOrNonIOS (): boolean {
    return !helpers.isIOS() || isStandalone()
  }

  getPermission (): NotificationPermission {
    if (!('Notification' in window)) {
      return 'denied'
    }

    return Notification.permission
  }

  async subscribe (): Promise<PushSubscription | null> {
    if (!this.isPushSupported()) {
      return null
    }

    const reg = await navigator.serviceWorker.getRegistration()
    if (!reg) {
      return null
    }

    const existing = await reg.pushManager.getSubscription()

    const subscription = existing ?? await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlToBuffer(import.meta.env.VITE_VAPID_PUBLIC_KEY)
    })

    await this.saveOnServer(subscription)

    return subscription
  }

  async unsubscribe (): Promise<void> {
    if (!this.isPushSupported()) {
      return
    }

    const reg = await navigator.serviceWorker.getRegistration()
    const subscription = await reg?.pushManager.getSubscription()

    if (!subscription) {
      return
    }

    try {
      await apiClient.delete('/api/me/push-subscriptions/', {
        params: { endpoint: subscription.endpoint }
      })
    } catch (err) {
      console.error('Failed to remove push subscription on server', err)
    }

    await subscription.unsubscribe()
  }

  // Used in the 401 interceptor: revoke the browser-side subscription only.
  // Skipping the BE DELETE avoids a second 401-and-hang on the same auth failure;
  // the orphan row dies via 410 cleanup on the next BE send attempt.
  async unsubscribeLocally (): Promise<void> {
    if (!this.isPushSupported()) {
      return
    }

    const reg = await navigator.serviceWorker.getRegistration()
    const subscription = await reg?.pushManager.getSubscription()

    await subscription?.unsubscribe()
  }

  async refreshSubscription (): Promise<void> {
    if (!this.isPushSupported() || this.getPermission() !== 'granted') {
      return
    }

    const reg = await navigator.serviceWorker.getRegistration()
    const subscription = await reg?.pushManager.getSubscription()

    if (!subscription) {
      await this.subscribe()
      return
    }

    await this.saveOnServer(subscription)
  }

  private async saveOnServer (subscription: PushSubscription): Promise<void> {
    const json = subscription.toJSON()

    if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
      return
    }

    await apiClient.post('/api/me/push-subscriptions/', {
      endpoint: json.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth }
    })
  }
}

export const pushService = new PushService()
